import { clampNights, keyOfName, km, normalizeName, totalNights, type LatLng, type Stop, type StopSource } from "./plannerLogic";

/**
 * What the route map reads off a country hub's guide payload (`exploreGuide`, an unmodelled JSON blob), as plain data.
 * Pure on purpose: the server renders the route list from it, and nothing here may pull the map or the app in.
 */

/**
 * Hubs that render the route map instead of the route cards. The other hubs are the control (`?routemap=1` previews the map on any of them).
 * Thailand is back on since 2026-10-28 (Robin), once phones went back to the in-page signup (useSaveRoute); it was off from 2026-10-21 for the design pass.
 * Japan and Vietnam since 2026-10-28 too, after Ilse's review of their previews (copy in explore/route-map/ in the KB).
 */
export const COUNTRY_ROUTE_MAP_COUNTRIES = new Set<string>(["thailand", "japan", "vietnam"]);

export type RouteStopData = StopSource & {
	/** a photo of the stop's own place, for a stop that is no destination of the hub (no card, so no `MapPoint`): the marker's, from the KB's media */
	photo?: string;
};

interface RawRoute {
	id: string;
	title: string;
	subtitle?: string;
	desc?: string;
	bestTime?: string;
	nights?: number;
	duration?: string | null;
	// Optional: the Redis guide cache can still hold a payload from before the backend sent them.
	stops?: Partial<RouteStopData>[];
}

interface RawMarker {
	slug: string;
	name: string;
	lat: number;
	lng: number;
	type?: string | null;
	/** the destination's editorial label ("Must-see", "Classic", ...); the payload from before the backend sent it has none */
	label?: string | null;
	/** the destination's own picture (the card's: its guide's hero, else the KB's gallery hero); absent when it has none, and from before the backend sent it */
	photo?: string;
}

interface RawDestinationItem {
	name?: string;
	slug?: string;
	kind?: string;
	blurb?: string;
	photo?: string;
	nights?: string;
}

// Optional: the dossier's `## Regions` table reaches the payload with the next KB sync (tools/build_guide_documents.py).
interface RawRegion {
	name?: string;
	slugs?: string[];
}

/**
 * The map's content in the page's language: `guide.routeMap` (KB `explore/route-map/<country>.json` + `_common.json`, picked to the reader's language by
 * `exploreGuide`). Every field is optional: a hub whose KB has no file yet, and the Redis guide cache that can outlive a deploy, both send none, and the
 * map then reads the English fields the payload always had.
 */
export interface RawRouteMap {
	/** the country's name in the page's language */
	name?: string;
	/** The country with the preposition or article the language puts before it, for French, Italian and Portuguese (en Thaïlande / au Japon, la Thaïlande / le Japon, pela Tailândia / pelo Japão) */
	forms?: { in?: string; the?: string; of?: string; by?: string };
	/** dossier region name -> its name here */
	regions?: Record<string, string>;
	/** trip id -> the route's name and its "why pick it" line */
	routes?: Record<string, { name?: string; pitch?: string }>;
	/** destination slug -> its one line */
	destinations?: Record<string, string>;
	/** trip-length group ("1week") -> label; destination type ("City") -> label */
	groups?: Record<string, string>;
	types?: Record<string, string>;
}

export interface RouteMapGuide {
	slug?: string;
	/** the country's English name: what createTripFromRoute takes for a stop's country */
	name?: string;
	routes?: RawRoute[];
	mapMarkers?: RawMarker[];
	destinations?: { items?: RawDestinationItem[] }[];
	regions?: RawRegion[];
	routeMap?: RawRouteMap;
}

export interface MapPoint {
	slug: string;
	name: string;
	lat: number;
	lng: number;
	/** the marker's ExploreDestination type ("City", "Island", ...), else the destination's kind; null when neither is known */
	type: string | null;
	blurb: string;
	/** the type in the page's language ("Island" / "Insel"); the English type when the payload has no copy */
	typeLabel: string;
	photo: string;
	/** the destination's label is "Must-see": its name plate stays up when the map is zoomed out */
	must: boolean;
	/** the nights a stop here starts with: the card's "3 nights", else DEFAULT_STOP_NIGHTS */
	nights: number;
}

/** Where an added stop came from, as `countrymap_add` reports it. "tile" is the empty state's photo tiles. */
export type AddSource = "sheet" | "near" | "map" | "tile";

export const DEFAULT_STOP_NIGHTS = 2;

/** `ExploreDestination.label` of the destinations a country is known for */
const MUST_SEE = "Must-see";

/** "3 nights" is 3, a range ("2-3 nights") its lower end, and a card with no figure DEFAULT_STOP_NIGHTS. */
export function parseNights(raw: string | undefined): number {
	const n = Number(/\d+/.exec(raw ?? "")?.[0]);
	return n > 0 ? clampNights(n) : DEFAULT_STOP_NIGHTS;
}

/** A point as the stop it becomes when it is added to My route; the destination's slug is its key. */
export const stopOfPoint = (point: MapPoint, country: string): Stop => ({
	key: point.slug,
	name: point.name,
	country,
	lat: point.lat,
	lng: point.lng,
	nights: point.nights,
});

export const DURATION_GROUPS = ["1week", "10days", "2weeks", "3weeks"] as const;
export type DurationGroup = (typeof DURATION_GROUPS)[number];

/** 1 week up to 8 nights, 10 days up to 11, 2 weeks up to 16, then 3 weeks and more */
export const durationGroup = (nights: number): DurationGroup => (nights <= 8 ? "1week" : nights <= 11 ? "10days" : nights <= 16 ? "2weeks" : "3weeks");

export interface MapRoute {
	id: string;
	title: string;
	subtitle: string;
	desc: string;
	/** the one line on why to pick the route (KB route-map copy); empty when the payload has none: `desc` stands in */
	pitch: string;
	bestTime: string;
	nights: number;
	duration: string | null;
	/** in trip order; empty for a route the backend could not place on the map */
	stops: RouteStopData[];
	group: DurationGroup;
}

export interface RouteMapRegion {
	/** the dossier's name for it: what the points are matched on */
	name: string;
	/** its name in the page's language */
	label: string;
	slugs: string[];
}

/** What the strings of the map put where they say the country: "Plan {country} in Stippl", and where a language needs a preposition or article, "{in}" (en Thaïlande), "{the}", "{of}", "{by}". */
export interface CountryNames {
	country: string;
	in: string;
	the: string;
	of: string;
	by: string;
}

export interface RouteMapData {
	/** the country's English name */
	country: string;
	/** the country's name in the page's language (the KB's, else the hub's own), and French's forms of it */
	names: CountryNames;
	/** the label of each trip-length chip ("all", "1week", ...) in the page's language, where the KB has one */
	groupLabels: Record<string, string>;
	/** the destinations that have a guide page (`guide.destinations`), in its order: what the hub puts first. Nothing else is drawn as a place. */
	points: MapPoint[];
	/** only the regions that list one of the points: none, and the add sheet has no region chips */
	regions: RouteMapRegion[];
	routes: MapRoute[];
}

const isPlace = (s: Partial<RouteStopData> | undefined): s is RouteStopData =>
	!!s && typeof s.name === "string" && Number.isFinite(s.lat) && Number.isFinite(s.lng);

/**
 * The route map is on when the hub is allowlisted (or `preview`: the page's `?routemap=1`) AND the payload carries stops. The fields are
 * optional on the wire because the Redis guide cache can outlive a deploy; until it turns over, the hub keeps the route cards.
 */
export const shouldRenderRouteMap = (guide: RouteMapGuide | null | undefined, preview = false): boolean =>
	!!guide?.slug && (preview || COUNTRY_ROUTE_MAP_COUNTRIES.has(guide.slug)) && !!guide.routes?.some((r) => r.stops?.length);

/**
 * The places of the map: `mapMarkers` (name, coordinates, type, label, photo) joined by slug with the destination cards (kind, blurb, photo), and
 * only those that have a card. A marker with no card has no line and no guide page, so the map does not draw it as a place (the card's
 * "Guide" button would open a 404); a route can still stop there, and its stop is drawn as a numbered disc. That stop keeps the marker's
 * photo (`RouteStopData.photo`), so it is a photo card on the map and a photo in the lists like any other. Every route stays in the list,
 * one without stops included: a card that cannot draw itself can still be read and saved, and the cards it replaces showed it.
 */
export function buildRouteMapData(guide: RouteMapGuide, hubName = ""): RouteMapData {
	const copy = guide.routeMap ?? {};
	const markers = new Map<string, RawMarker>();
	for (const m of guide.mapMarkers ?? []) if (Number.isFinite(m.lat) && Number.isFinite(m.lng)) markers.set(m.slug, m);
	const items = new Map<string, RawDestinationItem>();
	for (const group of guide.destinations ?? []) {
		for (const item of group.items ?? []) if (item.slug && markers.has(item.slug)) items.set(item.slug, item);
	}
	const points = [...items].map(([slug, item]): MapPoint => {
		const m = markers.get(slug)!;
		return {
			slug,
			name: m.name,
			lat: m.lat,
			lng: m.lng,
			type: m.type || item.kind || null,
			// the KB's line for the place (Ilse's), else the card's own blurb
			blurb: copy.destinations?.[slug] || item.blurb || "",
			typeLabel: (copy.types && (copy.types[m.type ?? ""] ?? copy.types[item.kind ?? ""])) || m.type || item.kind || "",
			// the card's photo, else the marker's: the KB writes a card without one as ""
			photo: item.photo || m.photo || "",
			must: m.label === MUST_SEE,
			nights: parseNights(item.nights),
		};
	});
	const placed = new Set(items.keys());
	const regions = (guide.regions ?? []).flatMap((r) => {
		const slugs = (r.slugs ?? []).filter((slug) => placed.has(slug));
		return r.name && slugs.length ? [{ name: r.name, label: copy.regions?.[r.name] || r.name, slugs }] : [];
	});
	// markers that are no place on the map (no card) but have a picture: what a stop there shows
	const cardless = [...markers.values()].filter((m) => m.photo && !items.has(m.slug));
	const routes = (guide.routes ?? []).map((r): MapRoute => {
		const stops = (r.stops ?? []).filter(isPlace).map((s) => {
			const point = pointOfStop(s, points);
			const photo = point ? point.photo : pointOfStop(s, cardless)?.photo;
			return { ...s, country: s.country ?? "", nights: clampNights(s.nights), ...(photo ? { photo } : {}) };
		});
		const nights = Number.isFinite(r.nights) && r.nights! > 0 ? r.nights! : totalNights(stops);
		return {
			id: r.id,
			title: copy.routes?.[r.id]?.name || r.title,
			subtitle: r.subtitle ?? "",
			desc: r.desc ?? "",
			pitch: copy.routes?.[r.id]?.pitch || "",
			bestTime: r.bestTime ?? "",
			nights,
			duration: r.duration ?? null,
			stops,
			group: durationGroup(nights),
		};
	});
	const country = copy.name || hubName || guide.name || "";
	const forms = copy.forms ?? {};
	const names: CountryNames = { country, in: forms.in || country, the: forms.the || country, of: forms.of || country, by: forms.by || country };
	return { country: guide.name ?? "", names, groupLabels: copy.groups ?? {}, points, regions, routes };
}

/** How far from a stop a point can be and still be the same place, when the names do not agree (Ko Samui / Koh Samui) */
const SAME_PLACE_KM = 8;

/** The map point a stop is: the one with its name, else the nearest within a few km. Null for a stop off the map's points (Isan). */
export function pointOfStop<P extends LatLng & { name: string }>(stop: StopSource, points: P[]): P | null {
	const name = normalizeName(stop.name);
	const byName = points.find((p) => normalizeName(p.name) === name);
	if (byName) return byName;
	let nearest: P | null = null;
	let least = SAME_PLACE_KM;
	for (const p of points) {
		const d = km(stop, p);
		if (d <= least) {
			least = d;
			nearest = p;
		}
	}
	return nearest;
}

/**
 * A dossier's best time ("Nov–Apr") with its months in the page's language ("nov.–apr." is "Nov–Apr" again once the dot is gone). A value that is
 * not "Mon–Mon" (a translated one, "Nov–Feb, then Apr") is left as it came.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function bestTimeIn(raw: string, locale: string): string {
	const m = /^([A-Z][a-z]{2})\s*[–-]\s*([A-Z][a-z]{2})$/.exec(raw.trim());
	const a = m ? MONTHS.indexOf(m[1]) : -1;
	const b = m ? MONTHS.indexOf(m[2]) : -1;
	if (a < 0 || b < 0) return raw;
	try {
		const fmt = new Intl.DateTimeFormat(locale === "en" ? "en-GB" : locale, { month: "short" });
		const name = (i: number) => {
			const t = fmt.format(new Date(2026, i, 15)).replace(".", "");
			return t.charAt(0).toUpperCase() + t.slice(1);
		};
		return `${name(a)}–${name(b)}`;
	} catch {
		return raw;
	}
}

/** "Bangkok · Chiang Mai · Phuket": the stops in order, a place twice in a row (two legs from one base) once */
export const stopsLine = (route: Pick<MapRoute, "stops" | "subtitle">, separator = " · "): string =>
	route.stops.length
		? route.stops
				.filter((s, i) => s.name !== route.stops[i - 1]?.name)
				.map((s) => s.name)
				.join(separator)
		: route.subtitle;

/** A route's photo, and the type of the place it is of (for the tint of the card while it loads, and when it has none) */
export interface RouteImage {
	/** the destination's slug; the name as a slug for a stop that is no destination */
	slug: string;
	photo: string;
	type: string | null;
}

/**
 * The photo that stands for each route: on its card in the list and behind the panel's header while it is on the map. The first of its stops
 * that has a photo and is not already another route's, so the cards do not all show Bangkok; else its last stop's (the demo's `routeImg`).
 * A stop that is no destination of the hub brings its own (`RouteStopData.photo`).
 */
export function routeImages(routes: MapRoute[], points: MapPoint[]): Map<string, RouteImage> {
	const used = new Set<string>();
	const out = new Map<string, RouteImage>();
	for (const route of routes) {
		const withPhoto = route.stops.flatMap((s) => {
			const point = pointOfStop(s, points);
			const photo = point?.photo || s.photo;
			return photo ? [{ slug: point?.slug ?? keyOfName(s.name), photo, type: point?.type ?? null }] : [];
		});
		const pick = withPhoto.find((p) => !used.has(p.slug)) ?? withPhoto[withPhoto.length - 1];
		if (pick) {
			used.add(pick.slug);
			out.set(route.id, pick);
		}
	}
	return out;
}

/** The photo behind the header while no route is on the map: a must-see with a photo, else the first with one. */
export const heroPoint = (points: MapPoint[]): MapPoint | null => points.find((p) => p.must && p.photo) ?? points.find((p) => p.photo) ?? null;
